import React, { useState } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { ArrowRight, Apple, Smartphone, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { getUTM, getSessionId, track } from "../../lib/tracking";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ROLE_KEYS = ["ruiter", "eigenaar", "trainer", "professional", "manege", "fokker", "stal"];

export const FinalCTA = () => {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("ruiter");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      const page = typeof window !== "undefined" ? window.location.pathname : "/";
      await axios.post(`${API}/waitlist`, {
        email,
        role,
        source: "landing",
        page,
        utm: getUTM(),
        session_id: getSessionId(),
      });
      track("cta_click", { label: "waitlist_submit", props: { role } });
      setDone(true);
      toast.success(t("finalCta.successToast"));
      setEmail("");
    } catch (err) {
      const msg =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        t("finalCta.errorGeneric");
      toast.error(typeof msg === "string" ? msg : t("finalCta.errorGeneric"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="waitlist"
      data-testid="final-cta-section"
      className="relative overflow-hidden bg-[hsl(var(--forest))] py-24 text-[hsl(var(--background))] lg:py-32 grain"
    >
      <div className="absolute -right-40 -top-40 h-[28rem] w-[28rem] rounded-full bg-[hsl(var(--sand))]/15 blur-3xl" />
      <div className="absolute -bottom-32 -left-20 h-[24rem] w-[24rem] rounded-full bg-white/5 blur-3xl" />

      <div className="relative mx-auto max-w-5xl px-6 text-center lg:px-10">
        <p className="text-xs uppercase tracking-[0.22em] text-[hsl(var(--sand))]">
          {t("finalCta.eyebrow")}
        </p>
        <h2
          id="download"
          className="mt-6 font-display text-5xl font-medium leading-[1.02] text-balance sm:text-6xl lg:text-[5.2rem]"
        >
          {t("finalCta.titleLine1")}
          <br />
          {t("finalCta.titleLine2Before")}{" "}
          <em className="italic text-[hsl(var(--sand))]">{t("finalCta.titleLine2Em")}</em>{" "}
          {t("finalCta.titleLine2After")}
        </h2>
        <p className="mx-auto mt-8 max-w-xl text-lg text-[hsl(var(--background))]/75">
          {t("finalCta.intro")}
        </p>

        <form
          onSubmit={submit}
          data-testid="waitlist-form"
          className="mx-auto mt-10 flex w-full max-w-xl flex-col gap-3 sm:flex-row"
        >
          <input
            type="email"
            required
            placeholder={t("finalCta.emailPlaceholder")}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            data-testid="waitlist-email-input"
            className="flex-1 rounded-full border border-white/15 bg-white/5 px-6 py-4 text-sm text-[hsl(var(--background))] placeholder:text-[hsl(var(--background))]/50 outline-none transition-all focus:border-[hsl(var(--sand))] focus:bg-white/10"
          />
          <select
            value={role}
            onChange={(e) => setRole(e.target.value)}
            data-testid="waitlist-role-select"
            className="rounded-full border border-white/15 bg-white/5 px-5 py-4 text-sm text-[hsl(var(--background))] outline-none transition-all focus:border-[hsl(var(--sand))]"
          >
            {ROLE_KEYS.map((rk) => (
              <option key={rk} value={rk} className="text-[hsl(var(--ink))]">
                {t(`finalCta.roles.${rk}`)}
              </option>
            ))}
          </select>
          <button
            type="submit"
            disabled={loading || done}
            data-testid="waitlist-submit-btn"
            className="inline-flex items-center justify-center gap-2 rounded-full bg-[hsl(var(--sand))] px-7 py-4 text-sm font-medium text-[hsl(var(--ink))] transition-all hover:bg-[hsl(var(--sand-dark))] disabled:opacity-70"
          >
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : done ? (
              t("finalCta.onList")
            ) : (
              <>
                {t("finalCta.submit")}
                <ArrowRight className="h-4 w-4" strokeWidth={1.8} />
              </>
            )}
          </button>
        </form>

        <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
          <a
            href="#"
            data-testid="cta-app-store"
            className="inline-flex items-center gap-2 rounded-full border border-white/20 px-6 py-3 text-sm transition-all hover:bg-white/5"
          >
            <Apple className="h-4 w-4" strokeWidth={1.6} />
            {t("finalCta.appStore")}
          </a>
          <a
            href="#"
            data-testid="cta-play-store"
            className="inline-flex items-center gap-2 rounded-full border border-white/20 px-6 py-3 text-sm transition-all hover:bg-white/5"
          >
            <Smartphone className="h-4 w-4" strokeWidth={1.6} />
            {t("finalCta.playStore")}
          </a>
        </div>
        <p className="mt-6 text-xs text-[hsl(var(--background))]/55">
          {t("finalCta.noSpam")}
        </p>
      </div>
    </section>
  );
};

export default FinalCTA;
